import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHILD_AVATARS, type ChildAvatarKey } from '../../utils/childAvatars';
import ArrowDownIcon from '../icons/ArrowDownIcon';

interface ChildAvatarSelectorProps {
  selected: ChildAvatarKey | null;
  onSelect: (key: ChildAvatarKey) => void;
  error?: string;
}

const popupVariants = {
  hidden: {
    opacity: 0,
    scaleY: 0.6,
    y: -4,
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },
  visible: {
    opacity: 1,
    scaleY: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0, 0, 0.2, 1] },
  },
};

export default function ChildAvatarSelector({
  selected,
  onSelect,
  error,
}: ChildAvatarSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedAvatar = CHILD_AVATARS.find((a) => a.key === selected);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`
          flex items-center gap-1 w-[79px] h-12 pl-1.5 pr-2 rounded-lg border bg-white transition-colors duration-200
          ${error ? 'border-red-500' : isOpen ? 'border-[#01A7C8]' : 'border-[#A9B7BE]'}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select avatar"
      >
        {selectedAvatar ? (
          <img
            src={selectedAvatar.src}
            alt={selectedAvatar.label}
            className="w-[34px] h-[34px] rounded-[4px] flex-shrink-0"
          />
        ) : (
          <div className="w-[34px] h-[34px] rounded-[4px] bg-[#D9D9D9] flex-shrink-0" />
        )}
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="flex-shrink-0"
        >
          <ArrowDownIcon fill={isOpen ? '#01A7C8' : '#4D5762'} />
        </motion.div>
      </button>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={popupVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={{ transformOrigin: 'top center' }}
            className="absolute top-full left-0 mt-1 z-50 bg-white border border-[#A9B7BE] rounded-xl shadow-lg p-2 w-[192px]"
          >
            <div className="grid grid-cols-3 gap-1.5">
              {CHILD_AVATARS.map((avatar) => {
                const isSelected = selected === avatar.key;
                return (
                  <button
                    key={avatar.key}
                    type="button"
                    onClick={() => {
                      onSelect(avatar.key);
                      setIsOpen(false);
                    }}
                    className={`
                      flex flex-col items-center justify-center p-1.5 rounded-lg border transition-colors duration-150
                      ${isSelected
                        ? 'border-[#01A7C8] bg-[#DBF7F8]'
                        : 'border-transparent hover:border-[#A9B7BE] hover:bg-[#F0FAFB] active:bg-[#E0F4F7]'
                      }
                    `}
                    aria-label={avatar.label}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <img src={avatar.src} alt={avatar.label} className="w-8 h-8" />
                    <span className="text-[10px] text-[#323B46] mt-0.5 leading-tight">
                      {avatar.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
